import { useMemo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BillingState,
  GetProgramEnrollmentPaymentPlansQuery,
  PaymentPlanStatus,
} from '@sandtech-hub-monorepo/ehub-client';

import { Button, cn } from '@sandtech-hub-monorepo/ui-kit';
import { AlertCircle, CheckCircle, ChevronRight, Info } from 'lucide-react';
import { differenceInDays, differenceInHours } from 'date-fns';
import { ExternalApplicationLinks } from '../../../../utils';
import { formatDate } from '../../../dateFormat';

export type AlertLevel = 'blue' | 'amber' | 'red' | 'success' | null;

// Context passed to message generator functions
export type PaymentMessageContext = {
  formattedDate: string;
  nextAmount: number;
  outstandingBalance: number;
  termBalance: number;
  isAllAccess: boolean;
};

// Message generator function type
type MessageGenerator = (ctx: PaymentMessageContext) => ReactNode;

// Messages configuration type
export type PaymentBannerMessages = {
  success: ReactNode;
  fallback: {
    red: ReactNode;
    amber: ReactNode;
    blue: ReactNode;
  };
  beforeFirst: {
    blue: MessageGenerator;
    amber: MessageGenerator;
    red: MessageGenerator;
  };
  firstPaymentMade?: {
    blue: MessageGenerator;
  };
  transitionNoOutstanding: {
    blue: MessageGenerator;
    amber: MessageGenerator;
    red: MessageGenerator;
  };
  transitionWithOutstanding: {
    blue: MessageGenerator;
    amber: MessageGenerator;
    red: MessageGenerator;
  };
  finalOutstanding?: {
    red: MessageGenerator;
  };
};

interface Props {
  paymentPlan?: GetProgramEnrollmentPaymentPlansQuery['viewer']['paymentPlans'][number];
  paymentFAQsLink: string;
  messages: PaymentBannerMessages;
  showPaymentPlanName?: boolean;
  showFAQLink?: boolean;
}

export const ProgramEnrollmentPaymentBanner = ({
  paymentPlan,
  paymentFAQsLink,
  messages,
  showPaymentPlanName = true,
  showFAQLink = true,
}: Props) => {
  const navigate = useNavigate();

  const { alertLevel, message, showBanner } = useMemo(() => {
    if (!paymentPlan) {
      return { showBanner: false };
    }
    if (
      !paymentPlan.status ||
      paymentPlan.status === PaymentPlanStatus.Cancelled
    ) {
      return { showBanner: false };
    }

    const { billingState, totalPaidAmount = 0, nextInvoice } = paymentPlan;
    const isAllAccess = paymentPlan.code === 'all-access';

    // If payment is completed or up to date, show success banner
    if (
      [
        BillingState.UpToDate,
        BillingState.InAdvance,
        BillingState.Completed,
      ].includes(billingState)
    ) {
      return {
        alertLevel: 'success' as AlertLevel,
        message: messages.success,
        showBanner: true,
      };
    }

    // Calculate due date from nextInvoice
    const dueDate = nextInvoice?.dueDate ? new Date(nextInvoice.dueDate) : null;
    if (!dueDate) {
      // Fallback to old logic if no due date
      const isError = [
        BillingState.InArrears,
        BillingState.Inactive,
        BillingState.Grace,
        BillingState.AtRisk,
        BillingState.Pending,
        BillingState.AwaitingPaymentConfirmation,
      ].includes(billingState);

      const level = isError ? 'red' : 'amber';
      return {
        alertLevel: level as AlertLevel,
        message: messages.fallback[level as 'red' | 'amber'],
        showBanner: true,
      };
    }

    const now = new Date();
    const daysUntilDue = differenceInDays(dueDate, now);
    const hoursUntilDue = differenceInHours(dueDate, now);

    // Determine alert level based on time until due
    let level: AlertLevel = null;
    if (daysUntilDue < 0) {
      level = 'red'; // Overdue
    } else if (hoursUntilDue < 48) {
      level = 'amber'; // Due soon (<48 hours)
    } else if (daysUntilDue <= 10) {
      level = 'blue'; // Coming up (10 days before)
    } else if (isAllAccess) {
      level = 'blue'; // Always show all-access banner
    } else {
      // Don't show banner yet
      return { showBanner: false };
    }

    // Convert cents to dollars
    const totalPaid = totalPaidAmount / 100;
    const nextAmount = (nextInvoice?.amount || 0) / 100;

    // Payment thresholds for SET: $15 first instalment, $60 per term
    // For central hub: Term 1: <$5 paid, Term 2: <$25 paid
    const isFirstPayment = totalPaid < 5;
    const hasPartialPayment = totalPaid >= 5 && totalPaid < 15; // First payment made but not full instalment
    const isTransition = totalPaid >= 15 && totalPaid < 60; // Paid first term, moving to next
    const outstandingBalance = Math.max(0, 60 - totalPaid);
    const termBalance = Math.max(0, 60 - totalPaid); // Remaining for semester
    const hasOutstanding = isTransition && outstandingBalance > 0;

    // Build message context
    const formattedDate = formatDate(dueDate);
    const ctx: PaymentMessageContext = {
      formattedDate,
      nextAmount,
      outstandingBalance,
      termBalance,
      isAllAccess,
    };

    let bannerMessage: ReactNode;

    // Determine scenario and generate message
    if (isFirstPayment) {
      const msgFn = messages.beforeFirst[level as 'blue' | 'amber' | 'red'];
      bannerMessage = msgFn(ctx);
    } else if (
      hasPartialPayment &&
      level === 'blue' &&
      messages.firstPaymentMade
    ) {
      // First payment made state (SET-specific)
      bannerMessage = messages.firstPaymentMade.blue(ctx);
    } else if (isTransition && !hasOutstanding) {
      const msgFn =
        messages.transitionNoOutstanding[level as 'blue' | 'amber' | 'red'];
      bannerMessage = msgFn(ctx);
    } else if (isTransition && hasOutstanding) {
      const msgFn =
        messages.transitionWithOutstanding[level as 'blue' | 'amber' | 'red'];
      bannerMessage = msgFn(ctx);
    } else if (level === 'red' && messages.finalOutstanding) {
      // Final outstanding balance state
      bannerMessage = messages.finalOutstanding.red(ctx);
    } else {
      // Fallback message
      bannerMessage = messages.fallback[level as 'blue' | 'amber' | 'red'];
    }

    return {
      alertLevel: level,
      message: bannerMessage,
      showBanner: true,
    };
  }, [paymentPlan, messages]);

  if (!showBanner) {
    return null;
  }

  const isError = alertLevel === 'red';
  const isSuccess = alertLevel === 'success';
  const isInfo = alertLevel === 'blue';
  const isWarning = alertLevel === 'amber';

  return (
    <div
      className={cn(
        'px-4 py-3 lg:py-1 flex flex-col sm:flex-row gap-2 lg:gap-4 items-center border w-full rounded-lg',
        isError ? 'border-border-error bg-destructive/10 text-destructive' : '',
        isSuccess ? 'border-success bg-success/10 text-success' : '',
        isInfo ? 'border-info bg-info/10 text-info' : '',
        isWarning ? 'border-warning bg-warning/10 text-warning' : ''
      )}
    >
      {isError && <AlertCircle className="stroke-destructive h-6 w-6" />}
      {isSuccess && <CheckCircle className="stroke-success min-h-6 min-w-6" />}
      {(isInfo || isWarning) && (
        <Info className="stroke-current min-h-6 min-w-6" />
      )}
      {showPaymentPlanName && <b>{paymentPlan?.definition?.name}</b>}
      <div className="text-sm">
        {message}{' '}
        {showFAQLink && !isSuccess && paymentPlan && (
          <span>
            Read our{' '}
            <Button
              className={`h-fit p-0 font-semibold underline ${
                isError
                  ? 'text-error'
                  : isWarning
                  ? 'text-warning'
                  : 'text-primary'
              }`}
              variant={'link'}
              onClick={() => navigate(paymentFAQsLink)}
            >
              payments FAQ
            </Button>
            .
          </span>
        )}
      </div>
      {paymentPlan?.billingState !== BillingState.Completed && (
        <Button
          variant={
            isError
              ? 'ghostDestructive'
              : isInfo
              ? 'ghostInfo'
              : isWarning
              ? 'ghostWarning'
              : 'ghostSuccess'
          }
          className="mx-auto sm:ml-auto sm:mr-0 p-0"
          onClick={() =>
            window.location.replace(
              paymentPlan?.viewUrl || ExternalApplicationLinks.payments
            )
          }
        >
          {isError && (paymentPlan ? 'Pay now' : 'Activate')}
          {isSuccess && 'Make a Payment'}
          {(isInfo || isWarning) && 'Make payment now'}
          <ChevronRight />
        </Button>
      )}
    </div>
  );
};
